/**
 * ShowRoom — internal admin console for the structured show conversation.
 *
 * Layout:
 *   +-------------------------+-------------------+------------------+
 *   |  Agent inboxes (grid)   |  Show Transcript  | Producer Console |
 *   |  nova / orion / vega    |   (show_ready     |  Topic setter    |
 *   |  atlas / producer /     |    + admin mode   |  Route to …      |
 *   |  quill / scribe / delta |    toggle)        |  Close segment   |
 *   +-------------------------+-------------------+------------------+
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import AgentInbox from './AgentInbox';
import ShowTranscript from './ShowTranscript';
import ProducerConsole from './ProducerConsole';
import WireDesk from '../wire/WireDesk';

const BG = 'linear-gradient(180deg, #0b1220 0%, #0a111c 100%)';
const CARD_BORDER = 'rgba(120,150,200,0.2)';

const AGENT_ORDER = ['producer', 'nova', 'orion', 'vega', 'atlas', 'scribe', 'quill', 'delta'];

export default function ShowRoom({ onNavigate }) {
  const [episode, setEpisode] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [inboxes, setInboxes] = useState({});
  const [adminMode, setAdminMode] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  // Poll inboxes + transcript every 3s when an episode is live.
  const refresh = useCallback(async () => {
    if (!episode?.episode_id) return;
    try {
      const [tRes, iRes, eRes] = await Promise.all([
        api.get(`/api/show/episodes/${episode.episode_id}/transcript`, {
          params: { visibility: adminMode ? 'all' : 'show_ready', limit: 300 },
        }),
        api.get('/api/show/agents/inboxes'),
        api.get(`/api/show/episodes/${episode.episode_id}`),
      ]);
      setTranscript(tRes.data.messages || []);
      const map = {};
      (iRes.data.agents || []).forEach((a) => { map[a.agent] = a; });
      setInboxes(map);
      setEpisode(eRes.data.episode);
    } catch (e) {
      // Soft-fail on polling — preview may blip.
      // eslint-disable-next-line no-console
      console.warn('show refresh', e?.message);
    }
  }, [episode?.episode_id, adminMode]);

  useEffect(() => {
    if (!episode) return undefined;
    refresh();
    const t = setInterval(refresh, 3000);
    return () => clearInterval(t);
  }, [episode, refresh]);

  const startEpisode = async (title) => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post('/api/show/episodes', { title: title || 'New show' });
      setEpisode(r.data.episode);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'create failed');
    } finally { setBusy(false); }
  };

  const runTopic = async (body) => {
    if (!episode) return;
    setBusy(true); setErr(null);
    try {
      await api.post(`/api/show/episodes/${episode.episode_id}/topic`, body);
      await refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'topic failed');
    } finally { setBusy(false); }
  };

  const stepForward = async (overrideNext) => {
    if (!episode) return;
    setBusy(true);
    try {
      await api.post('/api/show/step', {
        episode_id: episode.episode_id,
        override_next: overrideNext || null,
      });
      await refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'step failed');
    } finally { setBusy(false); }
  };

  const routeTo = async (target, directive) => {
    if (!episode) return;
    setBusy(true);
    try {
      await api.post('/api/show/route-to', {
        episode_id: episode.episode_id,
        target,
        directive: directive || null,
      });
      await refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'route failed');
    } finally { setBusy(false); }
  };

  const closeSegment = async () => {
    if (!episode) return;
    await api.post(`/api/show/episodes/${episode.episode_id}/close-segment`);
    await refresh();
  };

  const runPanelRound = async () => {
    if (!episode) return;
    setBusy(true);
    try {
      await api.post(`/api/show/episodes/${episode.episode_id}/panel-round`, {});
      await refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'panel round failed');
    } finally {
      setBusy(false);
    }
  };

  const sortedInboxes = useMemo(
    () => AGENT_ORDER.map((a) => inboxes[a] || { agent: a, status: 'idle', incoming: [], sent: [] }),
    [inboxes],
  );

  return (
    <div data-testid="show-room" style={{
      background: BG, minHeight: '100vh', color: '#e5ebf3',
      fontFamily: '-apple-system, "Segoe UI", Roboto, sans-serif',
      padding: '22px 28px 40px',
    }}>
      {/* Header */}
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, gap: 10, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: '0.32em', color: '#d4a574' }}>
            SIGNAL · INTERNAL SHOW ROOM
          </div>
          <div style={{ fontSize: 24, fontWeight: 300, marginTop: 4 }}>
            {episode ? episode.title : 'No active episode'}
          </div>
          {episode && (
            <div style={{ fontSize: 11, color: 'rgba(200,210,230,0.55)', marginTop: 2, fontFamily: 'ui-monospace, Menlo, monospace' }}>
              {episode.episode_id} · {episode.active_segment || 'no segment'} · current: {episode.current_speaker || '—'} · next: {episode.next_speaker || '—'} · status: {episode.status}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!episode && (
            <button
              type="button"
              onClick={() => startEpisode(`Show ${new Date().toISOString().slice(11, 16)}Z`)}
              disabled={busy}
              data-testid="start-episode-btn"
              style={btnPrimary}
            >
              {busy ? '…' : '▶ NEW EPISODE'}
            </button>
          )}
          {onNavigate && (
            <button type="button" onClick={() => onNavigate('nova')} style={btnGhost} data-testid="show-room-back">
              ← BACK
            </button>
          )}
        </div>
      </header>

      {err && (
        <div
          data-testid="show-error"
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '12px 14px',
            background: 'rgba(220,60,60,0.10)',
            border: '1px solid rgba(220,60,60,0.45)',
            borderRadius: 8,
            marginBottom: 14,
            color: '#fca5a5',
            fontSize: 12,
          }}
        >
          <div style={{
            width: 26, height: 26, borderRadius: 999,
            border: '1px solid rgba(248,113,113,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fca5a5', fontWeight: 700, flexShrink: 0,
          }}>!</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: 'ui-monospace, Menlo, monospace',
              fontSize: 11, letterSpacing: '0.18em',
              color: '#fca5a5', textTransform: 'uppercase', marginBottom: 2,
            }}>
              Producer offline — click to retry
            </div>
            <div style={{
              fontFamily: 'Georgia, serif',
              fontSize: 11.5, color: 'rgba(248,180,180,0.85)',
              wordBreak: 'break-word',
            }}>
              {err}
            </div>
          </div>
          <button
            type="button"
            data-testid="show-error-retry"
            onClick={() => { setErr(null); refresh(); }}
            disabled={busy}
            style={{
              fontFamily: 'ui-monospace, Menlo, monospace',
              fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase',
              color: '#fca5a5', background: 'rgba(248,113,113,0.12)',
              border: '1px solid rgba(248,113,113,0.45)',
              padding: '6px 12px', borderRadius: 4, cursor: 'pointer',
              opacity: busy ? 0.5 : 1, fontWeight: 600,
            }}
          >
            {busy ? '…' : 'RETRY'}
          </button>
        </div>
      )}

      {/* Main grid */}
      {episode ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(360px, 1.1fr) minmax(420px, 1.4fr) minmax(320px, 1fr)',
          gap: 14,
          alignItems: 'start',
        }}>
          <section data-testid="show-inboxes" style={cardPanel}>
            <div style={panelLabel}>AGENT INBOXES</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {sortedInboxes.map((box) => (
                <AgentInbox key={box.agent} inbox={box} highlighted={box.agent === episode.current_speaker} />
              ))}
            </div>
          </section>

          <section data-testid="show-transcript-section" style={cardPanel}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={panelLabel}>{adminMode ? 'ADMIN · FULL TRANSCRIPT' : 'PUBLIC · SHOW TRANSCRIPT'}</span>
              <div style={{ display: 'flex', gap: 4 }}>
                {['admin', 'public'].map((k) => (
                  <button
                    key={k}
                    type="button"
                    data-testid={`transcript-mode-${k}`}
                    onClick={() => setAdminMode(k === 'admin')}
                    style={{
                      ...pillBtn,
                      background: (adminMode ? 'admin' : 'public') === k ? 'rgba(212,165,116,0.16)' : 'transparent',
                      color: (adminMode ? 'admin' : 'public') === k ? '#d4a574' : 'rgba(200,210,230,0.5)',
                      borderColor: (adminMode ? 'admin' : 'public') === k ? '#d4a57455' : CARD_BORDER,
                    }}
                  >
                    {k.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <ShowTranscript messages={transcript} adminMode={adminMode} />
          </section>

          <section data-testid="show-producer-console" style={cardPanel}>
            <div style={panelLabel}>PRODUCER CONSOLE</div>
            <ProducerConsole
              episode={episode}
              busy={busy}
              onTopic={runTopic}
              onStep={stepForward}
              onRouteTo={routeTo}
              onCloseSegment={closeSegment}
              onPanelRound={runPanelRound}
            />
          </section>
        </div>
      ) : (
        <div data-testid="show-empty" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14, alignItems: 'start' }}>
          <div style={{ height: 600, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <WireDesk />
          </div>
          <div style={{ color: 'rgba(200,210,230,0.5)', fontSize: 14, fontFamily: 'Georgia, serif', fontStyle: 'italic', textAlign: 'center', padding: '40px 0', alignSelf: 'center' }}>
            Click <strong style={{ color: '#d4a574', fontStyle: 'normal' }}>▶ NEW EPISODE</strong> to begin a segment from a Wire topic, or browse the news desk.
          </div>
        </div>
      )}
    </div>
  );
}

const cardPanel = {
  background: 'rgba(17, 32, 52, 0.55)',
  border: `1px solid ${CARD_BORDER}`,
  borderRadius: 12,
  padding: '16px 18px',
  minHeight: 420,
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
};
const panelLabel = {
  fontSize: 10,
  letterSpacing: '0.28em',
  color: '#d4a574',
  marginBottom: 12,
  display: 'block',
};
const btnPrimary = {
  background: 'linear-gradient(180deg, #d4a57418, #d4a57408)',
  color: '#f3e5c3',
  border: '1px solid #d4a574',
  padding: '8px 16px',
  fontSize: 11,
  letterSpacing: '0.22em',
  borderRadius: 4,
  cursor: 'pointer',
  fontWeight: 600,
};
const btnGhost = {
  background: 'transparent',
  color: 'rgba(200,210,230,0.55)',
  border: `1px solid ${CARD_BORDER}`,
  padding: '8px 14px',
  fontSize: 11,
  letterSpacing: '0.22em',
  borderRadius: 4,
  cursor: 'pointer',
};
const pillBtn = {
  border: `1px solid ${CARD_BORDER}`,
  padding: '4px 10px',
  fontSize: 10,
  letterSpacing: '0.22em',
  borderRadius: 999,
  cursor: 'pointer',
  fontFamily: 'ui-monospace, Menlo, monospace',
};
